/**
 * Master Application Controller & UI Coordinator for Aim Assist Training Lab
 * Responsive glass UI, HUD overlays, dialogs, and game loop orchestration
 */

import { GAME_PRESETS, TRAINING_MODES, TRAINING_PROGRAMS, CROSSHAIR_STYLES, CROSSHAIR_COLORS, TARGET_COLORS, TARGET_SHAPES, TARGET_DEPTH_ZONES, MOTION_PATTERNS, WEAPON_TYPES, ARENA_THEMES } from './constants.js';
import { SettingsStore } from './settings-store.js';
import { SessionHistory } from './session-history.js';
import { AudioManager } from './audio-manager.js';
import { SceneManager } from './scene-manager.js';
import { WeaponController } from './weapon-controller.js';
import { TargetManager } from './target-manager.js';
import { ShootingSystem } from './shooting-system.js';
import { TrainingEngine } from './training-engine.js';
import { InputController } from './input-controller.js';
import { getReflexTier } from './benchmarks-coaching.js';
import { mountLeaderboard, checkAndPromptScore } from '../../core/leaderboard.js';

export class AimAssistApp {
  constructor(containerId = 'aim-training-app') {
    this.container = document.getElementById(containerId);
    if (!this.container) return;

    this.isAr = document.documentElement.lang !== 'en';

    // Core Subsystems
    this.settingsStore = new SettingsStore();
    this.sessionHistory = new SessionHistory();
    this.audioManager = new AudioManager(this.settingsStore);

    this.sceneManager = null;
    this.weaponController = null;
    this.targetManager = null;
    this.shootingSystem = null;
    this.trainingEngine = null;
    this.inputController = null;

    this.animationFrameId = null;
    this.lastTime = performance.now();

    this.initDOM();
  }

  initDOM() {
    this.container.innerHTML = `
      <div class="aim-lab-wrapper">
        <!-- Top Toolbar -->
        <header class="aim-toolbar">
          <div class="aim-toolbar-group">
            <div class="aim-select-wrap">
              <label for="aim-mode-select" class="aim-field-label">${this.isAr ? 'نمط التدريب' : 'Drill Mode'}</label>
              <select id="aim-mode-select" class="form-select aim-toolbar-select">
                ${Object.values(TRAINING_MODES).map((m) => `
                  <option value="${m.id}">${this.isAr ? m.name_ar : m.name_en}</option>
                `).join('')}
              </select>
            </div>

            <div class="aim-select-wrap">
              <label for="aim-game-select" class="aim-field-label">${this.isAr ? 'اللعبة المستهدفة' : 'Game Preset'}</label>
              <select id="aim-game-select" class="form-select aim-toolbar-select">
                ${Object.values(GAME_PRESETS).map((g) => `
                  <option value="${g.id}">${this.isAr ? g.name_ar : g.name_en}</option>
                `).join('')}
              </select>
            </div>

            <div class="aim-select-wrap">
              <label for="aim-program-select" class="aim-field-label">${this.isAr ? 'البرنامج الزمني' : 'Training Program'}</label>
              <select id="aim-program-select" class="form-select aim-toolbar-select">
                ${Object.values(TRAINING_PROGRAMS).map((p) => `
                  <option value="${p.id}">${this.isAr ? p.name_ar : p.name_en}</option>
                `).join('')}
              </select>
            </div>
          </div>

          <div class="aim-toolbar-actions">
            <button id="aim-btn-settings" class="btn-secondary aim-icon-btn" type="button" title="${this.isAr ? 'إعدادات الحساسية والتصويب' : 'Settings & Calibration'}" aria-label="${this.isAr ? 'الإعدادات' : 'Settings'}">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
            </button>
            <button id="aim-btn-history" class="btn-secondary aim-icon-btn" type="button" title="${this.isAr ? 'سجل المحاولات والإحصائيات' : 'History & Records'}" aria-label="${this.isAr ? 'السجل' : 'History'}">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            </button>
            <button id="aim-btn-fullscreen" class="aim-btn-fullscreen" type="button" title="${this.isAr ? 'تبديل وضع ملء الشاشة' : 'Toggle Fullscreen Mode'}" aria-label="${this.isAr ? 'ملء الشاشة' : 'Fullscreen'}">
              <svg class="aim-fs-icon-enter" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
              <svg class="aim-fs-icon-exit" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true" style="display:none;"><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
              <span id="aim-btn-fullscreen-label">${this.isAr ? 'ملء الشاشة' : 'Fullscreen'}</span>
            </button>
          </div>
        </header>

        <!-- 3D Arena Viewport Container -->
        <div id="aim-viewport" class="aim-viewport">
          <!-- WebGL Canvas mounts here -->

          <!-- Floating High-Visibility Fullscreen HUD Toggle -->
          <button id="aim-viewport-fs-btn" class="aim-viewport-fs-btn" type="button" title="${this.isAr ? 'ملء الشاشة' : 'Fullscreen'}" aria-label="${this.isAr ? 'ملء الشاشة' : 'Fullscreen'}">
            <svg class="aim-fs-icon-enter" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
            <svg class="aim-fs-icon-exit" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true" style="display:none;"><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
            <span class="aim-fs-badge-text">${this.isAr ? 'ملء الشاشة' : 'Fullscreen'}</span>
          </button>

          <!-- Viewport Center Hero Play Overlay (Start Drill directly over the 3D canvas) -->
          <div id="aim-viewport-hero" class="aim-viewport-hero">
            <span class="aim-hero-badge">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="22"/><line x1="2" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="22" y2="12"/></svg>
              <span>${this.isAr ? 'مختبر تدريب الرماية والسرعة' : 'Esports Aim Calibration Lab'}</span>
            </span>
            <h2 class="aim-hero-title">${this.isAr ? 'حسّن دقتك وردود أفعالك' : 'Master Precision & Reaction Speed'}</h2>
            <p class="aim-hero-subtitle">
              ${this.isAr ? 'محاكاة ثلاثية الأبعاد بفيزياء ارتداد حقيقية، أسلحة متعددة، وإحصائيات دقيقة مباشرة.' : 'Real-time 3D physics, authentic recoil mechanics, multiple weapon archetypes, and live telemetry.'}
            </p>

            <!-- Quick Weapon Selector -->
            <div class="aim-hero-weapon-bar">
              <span class="aim-hero-weapon-label">${this.isAr ? 'اختر السلاح:' : 'Choose Weapon:'}</span>
              <div class="aim-hero-weapon-pills">
                ${Object.values(WEAPON_TYPES).map((w) => `
                  <button type="button" class="aim-weapon-pill ${w.id === (this.settingsStore.get('selectedWeapon') || 'rifle') ? 'active' : ''}" data-weapon="${w.id}">
                    ${this.isAr ? w.name_ar : w.name_en}
                  </button>
                `).join('')}
              </div>
            </div>

            <!-- Start Drill CTA Button -->
            <button id="aim-btn-hero-play" class="aim-hero-play-btn" type="button">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><polygon points="5 3 19 12 5 21 5 3"/></svg>
              <span>${this.isAr ? 'ابدأ تمرين الرماية الآن' : 'Start Aim Drill Now'}</span>
            </button>

            <!-- Keyboard & Mouse Controls Legend -->
            <div class="aim-hero-controls-legend">
              <span class="aim-legend-item"><b>[LMB]</b> ${this.isAr ? 'إطلاق نار' : 'Fire'}</span>
              <span class="aim-legend-item"><b>[RMB]</b> ${this.isAr ? 'تصويب / زووم' : 'ADS Scope'}</span>
              <span class="aim-legend-item"><b>[R]</b> ${this.isAr ? 'تلقيم' : 'Reload'}</span>
              <span class="aim-legend-item"><b>[Space]</b> ${this.isAr ? 'قفز' : 'Jump'}</span>
              <span class="aim-legend-item"><b>[1-4]</b> ${this.isAr ? 'تبديل السلاح' : 'Switch Weapon'}</span>
              <span class="aim-legend-item"><b>[Esc]</b> ${this.isAr ? 'إيقاف مؤقت' : 'Pause'}</span>
            </div>
          </div>

          <!-- Tactical Crosshair Element -->
          <div id="aim-crosshair" class="aim-crosshair" aria-hidden="true"></div>

          <!-- Tactical Hit Marker Element -->
          <div id="aim-hit-marker" class="aim-hit-marker" aria-hidden="true"></div>

          <!-- Tactical ADS Optical Edge Vignette -->
          <div id="aim-ads-vignette" class="aim-ads-vignette" style="display: none;" aria-hidden="true"></div>

          <!-- Sniper Fullscreen Scope Reticle Overlay -->
          <div id="aim-sniper-scope" class="aim-sniper-scope" style="display: none;" aria-hidden="true">
            <div class="aim-sniper-reticle">
              <div class="aim-sniper-circle"></div>
              <div class="aim-sniper-inner-ring"></div>
              <div class="aim-sniper-cross-h"></div>
              <div class="aim-sniper-cross-v"></div>
              <div class="aim-sniper-center-dot"></div>
              <div class="aim-sniper-ticks">
                <span class="aim-scope-tick h1"></span>
                <span class="aim-scope-tick h2"></span>
                <span class="aim-scope-tick h3"></span>
                <span class="aim-scope-tick h4"></span>
                <span class="aim-scope-tick v1"></span>
                <span class="aim-scope-tick v2"></span>
                <span class="aim-scope-tick v3"></span>
                <span class="aim-scope-tick v4"></span>
              </div>
            </div>
          </div>

          <!-- Center Tactical Reload Indicator ("Goran" / دوران) -->
          <div id="aim-reload-indicator" class="aim-reload-indicator" style="display: none;" aria-hidden="true">
            <div class="aim-reload-ring-wrap">
              <svg class="aim-reload-svg" viewBox="0 0 52 52" width="52" height="52" fill="none">
                <circle class="aim-reload-bg-circle" cx="26" cy="26" r="22" fill="none" stroke="rgba(255, 255, 255, 0.16)" stroke-width="2" />
                <circle class="aim-reload-progress-circle" id="aim-reload-svg-bar" cx="26" cy="26" r="22" fill="none" stroke="#38bdf8" stroke-width="2.5" stroke-linecap="round" />
              </svg>
            </div>
            <div class="aim-reload-label">
              <svg class="aim-reload-glyph-spin" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
              </svg>
              <span>${this.isAr ? 'جاري التلقيم' : 'RELOADING'}</span>
            </div>
          </div>

          <!-- Top High-Visibility Esports Centered HUD Display -->
          <div class="aim-hud-overlay">
            <div class="aim-hud-stat score">
              <span class="aim-hud-label">${this.isAr ? 'النقاط' : 'Score'}</span>
              <span id="aim-hud-score" class="aim-hud-val">0</span>
            </div>
            <div class="aim-hud-stat timer">
              <span class="aim-hud-label">${this.isAr ? 'الوقت' : 'Time'}</span>
              <span id="aim-hud-timer" class="aim-hud-val">60s</span>
            </div>
            <div class="aim-hud-stat acc">
              <span class="aim-hud-label">${this.isAr ? 'الدقة' : 'Accuracy'}</span>
              <span id="aim-hud-acc" class="aim-hud-val">0%</span>
            </div>
            <div class="aim-hud-stat ammo">
              <span class="aim-hud-label">${this.isAr ? 'الذخيرة' : 'Ammo'}</span>
              <span id="aim-hud-ammo" class="aim-hud-val">30</span>
              <div id="aim-hud-ammo-pips" class="aim-ammo-pips-strip"></div>
            </div>
          </div>

          <!-- Adaptive Difficulty HUD Toast -->
          <div id="aim-adaptive-toast" class="aim-adaptive-toast" aria-live="polite"></div>

          <!-- Countdown Prompt Overlay -->
          <div id="aim-countdown-overlay" class="aim-countdown-overlay" style="display: none;">
            <div id="aim-countdown-number" class="aim-countdown-number">3</div>
            <p class="aim-countdown-hint">${this.isAr ? 'انقر على الشاشة لقفل مؤشر الماوس والتصويب' : 'Click screen to lock cursor and aim'}</p>
          </div>

          <!-- Quick Reaction Cue Prompt Banner -->
          <div id="aim-reaction-prompt" class="aim-reaction-prompt" style="display: none;">
            <span id="aim-reaction-text" class="aim-reaction-text">WAIT...</span>
          </div>

          <!-- Pause Menu Modal -->
          <div id="aim-pause-menu" class="aim-pause-menu" style="display: none;">
            <h2 class="aim-pause-title">${this.isAr ? 'التدريب متوقف مؤقتاً' : 'Training Paused'}</h2>
            <div class="aim-pause-buttons">
              <button id="aim-btn-resume" class="btn-primary" type="button">${this.isAr ? 'استئناف التمرين' : 'Resume Drill'}</button>
              <button id="aim-btn-restart" class="btn-secondary" type="button">${this.isAr ? 'إعادة المحاولة' : 'Restart Drill'}</button>
              <button id="aim-btn-pause-settings" class="btn-secondary" type="button">${this.isAr ? 'تعديل الإعدادات' : 'Adjust Settings'}</button>
            </div>
          </div>

          <!-- Mobile Virtual Controls Overlay -->
          <div id="aim-mobile-controls" class="aim-mobile-controls" style="display: none;">
            <div id="aim-touch-stick" class="aim-touch-stick" aria-label="Virtual Joystick">
              <div id="aim-touch-knob" class="aim-touch-knob"></div>
            </div>
            <div id="aim-touch-look" class="aim-touch-look" aria-label="Touch Aiming Area"></div>
            <div class="aim-touch-actions">
              <button id="aim-btn-touch-fire" class="aim-touch-btn fire-btn" type="button" aria-label="Fire">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3"/><line x1="12" y1="3" x2="12" y2="7"/><line x1="12" y1="17" x2="12" y2="21"/><line x1="3" y1="12" x2="7" y2="12"/><line x1="17" y1="12" x2="21" y2="12"/></svg>
              </button>
              <button id="aim-btn-touch-ads" class="aim-touch-btn" type="button" aria-label="Aim Down Sights">ADS</button>
              <button id="aim-btn-touch-reload" class="aim-touch-btn" type="button" aria-label="Reload">R</button>
            </div>
          </div>
        </div>

        <!-- Live Performance Telemetry Dashboard & Quick Controls -->
        <section class="aim-telemetry-panel" aria-label="${this.isAr ? 'لوحة القياسات الحية والأداء' : 'Live Performance Telemetry'}">
          <header class="aim-telemetry-header">
            <h2 class="aim-telemetry-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
              <span>${this.isAr ? 'القياسات الحية والتحكم بالأهداف' : 'Live Telemetry & Target Mechanics'}</span>
            </h2>
            <div class="aim-live-badge">
              <span class="aim-live-dot"></span>
              <span>${this.isAr ? 'مراقبة حية للمسار' : 'Real-time Tracking'}</span>
            </div>
          </header>

          <!-- 6-Metric Live Counters Grid -->
          <div class="aim-telemetry-grid">
            <div class="aim-live-card hits">
              <span class="aim-live-label">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
                ${this.isAr ? 'الإصابات الناجحة' : 'Confirmed Hits'}
              </span>
              <span id="aim-live-hits" class="aim-live-num">0</span>
              <span class="aim-live-sub">${this.isAr ? 'الطلقات المصيبة' : 'Target impacts'}</span>
            </div>

            <div class="aim-live-card misses">
              <span class="aim-live-label">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                ${this.isAr ? 'الطلقات الضائعة' : 'Missed Shots'}
              </span>
              <span id="aim-live-misses" class="aim-live-num">0</span>
              <span class="aim-live-sub">${this.isAr ? 'خارج محيط الهدف' : 'Off-target'}</span>
            </div>

            <div class="aim-live-card acc">
              <span class="aim-live-label">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.49 2 2 6.49 2 12s4.49 10 10 10 10-4.49 10-10S17.51 2 12 2zm1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/></svg>
                ${this.isAr ? 'نسبة الدقة الحية' : 'Live Accuracy'}
              </span>
              <span id="aim-live-acc" class="aim-live-num">0%</span>
              <div class="aim-acc-bar">
                <div id="aim-live-acc-bar" class="aim-acc-fill"></div>
              </div>
            </div>

            <div class="aim-live-card speed">
              <span class="aim-live-label">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                ${this.isAr ? 'متوسط سرعة الاقتناص' : 'Average TTK'}
              </span>
              <span id="aim-live-speed" class="aim-live-num">-- ms</span>
              <span class="aim-live-sub">${this.isAr ? 'سرعة رد الفعل والتقاط الهدف' : 'Acquisition latency'}</span>
            </div>

            <div class="aim-live-card streak">
              <span class="aim-live-label">
                <span>🔥</span>
                ${this.isAr ? 'التتابع المتتالي' : 'Active Streak'}
              </span>
              <span id="aim-live-streak" class="aim-live-num">0x</span>
              <span class="aim-live-sub">${this.isAr ? 'إصابات متتالية دون خطأ' : 'Consecutive hits'}</span>
            </div>

            <div class="aim-live-card headshot">
              <span class="aim-live-label">
                <span>🎯</span>
                ${this.isAr ? 'إصابات المركز / الرأس' : 'Headshots / Center'}
              </span>
              <span id="aim-live-bullseyes" class="aim-live-num">0</span>
              <span class="aim-live-sub">${this.isAr ? 'نقطة المنتصف بدقة 100%' : 'Direct bullseye'}</span>
            </div>
          </div>

          <!-- Live Shot Quality Feed Strip -->
          <div class="aim-shot-feed-wrap">
            <span class="aim-shot-feed-label">${this.isAr ? 'شريط تسلسل الطلقات:' : 'Shot Feed:'}</span>
            <div id="aim-shot-pills" class="aim-shot-pills-list">
              <span class="aim-shot-feed-empty">${this.isAr ? 'ابدأ التمرين لإظهار تسلسل الطلقات وسرعتها' : 'Start drill to log real-time shot tempo'}</span>
            </div>
          </div>

          <!-- Quick Direct Target & Motion Controls -->
          <div class="aim-quick-toolbar">
            <div class="aim-quick-item">
              <span class="aim-quick-label">${this.isAr ? 'حركة الأهداف' : 'Target Motion'}</span>
              <label class="aim-toggle-chip">
                <input id="aim-quick-moving-check" type="checkbox" checked>
                <span>${this.isAr ? 'تفعيل أهداف متحركة' : 'Enable Movement'}</span>
              </label>
            </div>

            <div class="aim-quick-item">
              <label for="aim-quick-shape-select" class="aim-quick-label">${this.isAr ? 'شكل ومجسم الهدف' : 'Target Archetype'}</label>
              <select id="aim-quick-shape-select" class="form-select">
                ${TARGET_SHAPES.map((s) => `
                  <option value="${s.id}">${this.isAr ? s.name_ar : s.name_en}</option>
                `).join('')}
              </select>
            </div>

            <div class="aim-quick-item">
              <label for="aim-quick-motion-select" class="aim-quick-label">${this.isAr ? 'نمط الحركة الفيزيائية' : 'Kinematic Motion'}</label>
              <select id="aim-quick-motion-select" class="form-select">
                ${MOTION_PATTERNS.map((p) => `
                  <option value="${p.id}">${this.isAr ? p.name_ar : p.name_en}</option>
                `).join('')}
              </select>
            </div>

            <div class="aim-quick-item">
              <label for="aim-quick-speed-select" class="aim-quick-label">${this.isAr ? 'سرعة تحرك الأهداف' : 'Target Speed'}</label>
              <select id="aim-quick-speed-select" class="form-select">
                <option value="0.75">${this.isAr ? 'بطيء (0.75x)' : 'Slow (0.75x)'}</option>
                <option value="1.0" selected>${this.isAr ? 'طبيعي متوازن (1.0x)' : 'Standard (1.0x)'}</option>
                <option value="1.35">${this.isAr ? 'سريع وتنافسي (1.35x)' : 'Fast (1.35x)'}</option>
                <option value="1.75">${this.isAr ? 'فائق المحترفين (1.75x)' : 'Hyper (1.75x)'}</option>
              </select>
            </div>

            <div class="aim-quick-item">
              <label for="aim-quick-range-select" class="aim-quick-label">${this.isAr ? 'نطاق المسافة والعمق' : 'Range Depth Zone'}</label>
              <select id="aim-quick-range-select" class="form-select">
                ${TARGET_DEPTH_ZONES.map((z) => `
                  <option value="${z.id}">${this.isAr ? z.name_ar : z.name_en}</option>
                `).join('')}
              </select>
            </div>
          </div>
        </section>

        <!-- Performance & Settings Dialog Modals -->
        <dialog id="aim-settings-dialog" class="aim-dialog">
          <form method="dialog" class="aim-dialog-card">
            <header class="aim-dialog-header">
              <h2 class="aim-dialog-title">${this.isAr ? 'إعدادات الحساسية والتصويب' : 'Sensitivity & Aim Calibration'}</h2>
              <button class="aim-dialog-close" type="submit" aria-label="${this.isAr ? 'إغلاق' : 'Close'}">✕</button>
            </header>
            
            <div class="aim-dialog-body">
              <!-- Sensitivity Section -->
              <fieldset class="aim-settings-group">
                <legend class="aim-group-title">${this.isAr ? 'حساسية الماوس ومعايرة الدوران' : 'Mouse Sensitivity & cm/360 Calibration'}</legend>
                <div class="aim-fields-grid">
                  <div class="form-group">
                    <label for="aim-sens-input" class="form-label">${this.isAr ? 'حساسية اللعبة' : 'Game Sensitivity'}</label>
                    <input id="aim-sens-input" type="number" step="0.01" min="0.01" max="100" class="form-input">
                  </div>
                  <div class="form-group">
                    <label for="aim-dpi-input" class="form-label">DPI</label>
                    <input id="aim-dpi-input" type="number" step="50" min="200" max="16000" class="form-input">
                  </div>
                  <div class="form-group">
                    <label class="form-label">${this.isAr ? 'المسافة لكل دورة كاملة' : 'Distance per 360°'}</label>
                    <div id="aim-cm360-val" class="aim-cm360-badge">-- cm/360</div>
                  </div>
                </div>

                <div class="aim-fields-grid" style="margin-block-start: var(--space-3);">
                  <div class="form-group">
                    <label for="aim-fov-input" class="form-label">${this.isAr ? 'مجال الرؤية (FOV)' : 'Field of View (FOV)'}: <span id="aim-fov-val">103°</span></label>
                    <input id="aim-fov-input" type="range" min="70" max="115" class="form-input">
                  </div>
                  <div class="form-group">
                    <label for="aim-ads-input" class="form-label">${this.isAr ? 'معامل حساسية السكوب (ADS)' : 'ADS Sensitivity Multiplier'}: <span id="aim-ads-val">0.85x</span></label>
                    <input id="aim-ads-input" type="range" min="0.4" max="1.5" step="0.05" class="form-input">
                  </div>
                </div>

                <div class="aim-checkbox-row">
                  <label class="aim-checkbox-label">
                    <input id="aim-invert-y" type="checkbox">
                    <span>${this.isAr ? 'عكس المحور الرأسي (Invert Y-Axis)' : 'Invert Vertical Pitch (Invert-Y)'}</span>
                  </label>
                  <label class="aim-checkbox-label">
                    <input id="aim-adaptive-check" type="checkbox">
                    <span>${this.isAr ? 'تفعيل الصعوبة التكيفية الذكية (Adaptive Difficulty)' : 'Enable Adaptive Difficulty'}</span>
                  </label>
                  <label class="aim-checkbox-label">
                    <input id="aim-follow-recoil" type="checkbox">
                    <span>${this.isAr ? 'مؤشر يتبع الارتداد (Follow Recoil Reticle)' : 'Follow Recoil Reticle (CS2 / VAL style)'}</span>
                  </label>
                </div>
              </fieldset>

              <!-- Target Archetypes & Motion Dynamics Section -->
              <fieldset class="aim-settings-group">
                <legend class="aim-group-title">${this.isAr ? 'مجسمات الأهداف والحركة الفيزيائية' : 'Target Archetypes & Motion'}</legend>
                <div class="aim-fields-grid">
                  <div class="form-group">
                    <label for="aim-shape-select" class="form-label">${this.isAr ? 'شكل ومجسم الهدف' : 'Target Archetype'}</label>
                    <select id="aim-shape-select" class="form-select">
                      ${TARGET_SHAPES.map((s) => `
                        <option value="${s.id}">${this.isAr ? s.name_ar : s.name_en}</option>
                      `).join('')}
                    </select>
                  </div>
                  <div class="form-group">
                    <label for="aim-range-zone-select" class="form-label">${this.isAr ? 'نطاق المسافة والعمق' : 'Range Depth Zone'}</label>
                    <select id="aim-range-zone-select" class="form-select">
                      ${TARGET_DEPTH_ZONES.map((z) => `
                        <option value="${z.id}">${this.isAr ? z.name_ar : z.name_en}</option>
                      `).join('')}
                    </select>
                  </div>
                  <div class="form-group">
                    <label for="aim-motion-select" class="form-label">${this.isAr ? 'نمط الحركة' : 'Motion Pattern'}</label>
                    <select id="aim-motion-select" class="form-select">
                      ${MOTION_PATTERNS.map((p) => `
                        <option value="${p.id}">${this.isAr ? p.name_ar : p.name_en}</option>
                      `).join('')}
                    </select>
                  </div>
                  <div class="form-group">
                    <label for="aim-speed-input" class="form-label">${this.isAr ? 'سرعة حركة الأهداف' : 'Target Speed'}: <span id="aim-speed-val">1.0x</span></label>
                    <input id="aim-speed-input" type="range" min="0.5" max="2.0" step="0.1" class="form-input">
                  </div>
                  <div class="form-group">
                    <label for="aim-size-input" class="form-label">${this.isAr ? 'حجم ومقياس الأهداف' : 'Target Scale'}: <span id="aim-size-val">1.0x</span></label>
                    <input id="aim-size-input" type="range" min="0.6" max="1.5" step="0.1" class="form-input">
                  </div>
                </div>

                <div class="aim-checkbox-row">
                  <label class="aim-checkbox-label">
                    <input id="aim-moving-check" type="checkbox">
                    <span>${this.isAr ? 'تفعيل الأهداف المتحركة بالمسارات المتغيرة' : 'Enable dynamic moving targets'}</span>
                  </label>
                </div>
              </fieldset>

              <!-- Crosshair Customizer -->
              <fieldset class="aim-settings-group">
                <legend class="aim-group-title">${this.isAr ? 'تخصيص مؤشر التصويب (Crosshair)' : 'Crosshair Customization'}</legend>
                <div class="aim-fields-grid">
                  <div class="form-group">
                    <label for="aim-cross-style" class="form-label">${this.isAr ? 'شكل المؤشر' : 'Reticle Style'}</label>
                    <select id="aim-cross-style" class="form-select">
                      ${CROSSHAIR_STYLES.map((s) => `
                        <option value="${s.id}">${this.isAr ? s.name_ar : s.name_en}</option>
                      `).join('')}
                    </select>
                  </div>
                  <div class="form-group">
                    <label for="aim-cross-color" class="form-label">${this.isAr ? 'لون المؤشر' : 'Reticle Color'}</label>
                    <select id="aim-cross-color" class="form-select">
                      ${CROSSHAIR_COLORS.map((c) => `
                        <option value="${c.hex}">${this.isAr ? c.name_ar : (c.name_en || c.name_ar)}</option>
                      `).join('')}
                    </select>
                  </div>
                  <div class="form-group">
                    <label for="aim-target-color-select" class="form-label">${this.isAr ? 'لون الأهداف' : 'Target Palette'}</label>
                    <select id="aim-target-color-select" class="form-select">
                      ${TARGET_COLORS.map((t) => `
                        <option value="${t.id}">${this.isAr ? t.name_ar : t.id}</option>
                      `).join('')}
                    </select>
                  </div>
                </div>
              </fieldset>

              <!-- Device Controls Selection -->
              <fieldset class="aim-settings-group">
                <legend class="aim-group-title">${this.isAr ? 'وضع التحكم والمدخلات' : 'Control Scheme'}</legend>
                <div class="aim-fields-grid">
                  <div class="form-group">
                    <label for="aim-control-mode-select" class="form-label">${this.isAr ? 'نوع جهاز الإدخال' : 'Input Device'}</label>
                    <select id="aim-control-mode-select" class="form-select">
                      <option value="desktop">${this.isAr ? 'ماوس ولوحة مفاتيح (Desktop Mouse & Keyboard)' : 'Mouse & Keyboard'}</option>
                      <option value="touch">${this.isAr ? 'شاشة لمسية وهواتف (Touch Controls)' : 'Touchscreen Controls'}</option>
                    </select>
                  </div>
                  <div class="form-group">
                    <label for="aim-touch-sens-y" class="form-label">${this.isAr ? 'حساسية اللمس الرأسية (Y)' : 'Touch Y-Sensitivity'}: <span id="aim-touch-sens-y-val">1.0x</span></label>
                    <input id="aim-touch-sens-y" type="range" min="0.4" max="2.0" step="0.1" class="form-input">
                  </div>
                  <div class="form-group">
                    <label for="aim-sound-vol" class="form-label">${this.isAr ? 'مستوى الصوت' : 'Sound Volume'}</label>
                    <input id="aim-sound-vol" type="range" min="0" max="1" step="0.05" class="form-input">
                  </div>
                </div>

                <div class="aim-checkbox-row" style="margin-block-start: var(--space-2);">
                  <label class="aim-checkbox-label">
                    <input id="aim-gyro-check" type="checkbox">
                    <span>${this.isAr ? 'تفعيل الجيروسكوب للهواتف (Gyro Aiming)' : 'Enable Mobile Gyroscope Aiming'}</span>
                  </label>
                </div>
              </fieldset>
            </div>

            <footer class="aim-dialog-footer">
              <button id="aim-btn-reset-settings" class="btn-secondary" type="button">${this.isAr ? 'استعادة الافتراضيات' : 'Reset to Defaults'}</button>
              <button class="btn-primary" type="submit">${this.isAr ? 'حفظ وإغلاق' : 'Save & Close'}</button>
            </footer>
          </form>
        </dialog>

        <!-- Results & Coaching Feedback Dialog -->
        <dialog id="aim-results-dialog" class="aim-dialog">
          <div class="aim-dialog-card aim-results-card">
            <header class="aim-dialog-header">
              <h2 class="aim-dialog-title">${this.isAr ? 'تحليل نتائج الجلسة والتدريب المتعمد' : 'Session Performance & Coaching'}</h2>
              <button id="aim-results-close-btn" class="aim-dialog-close" type="button" aria-label="${this.isAr ? 'إغلاق' : 'Close'}">✕</button>
            </header>

            <div class="aim-dialog-body">
              <!-- Tier Banner -->
              <div id="aim-tier-hero" class="aim-tier-hero">
                <span id="aim-tier-pill" class="aim-tier-pill">Recruit</span>
                <div class="aim-score-display">
                  <span id="aim-final-score" class="aim-score-number">0</span>
                  <span class="aim-score-sub">${this.isAr ? 'مجموع النقاط' : 'Total Score'}</span>
                </div>
                <div id="aim-best-badge" class="aim-best-badge" style="display: none;">
                  ★ ${this.isAr ? 'رقم قياسي شخصي جديد!' : 'New Personal Best!'}
                </div>
              </div>

              <!-- Metrics Grid -->
              <div class="aim-stats-grid">
                <div class="stat-item">
                  <span id="aim-res-acc" class="stat-value">0%</span>
                  <span class="stat-label">${this.isAr ? 'الدقة الإجمالية' : 'Overall Accuracy'}</span>
                </div>
                <div class="stat-item">
                  <span id="aim-res-hits" class="stat-value">0 / 0</span>
                  <span class="stat-label">${this.isAr ? 'الإصابات / الطلقات' : 'Hits / Shots'}</span>
                </div>
                <div class="stat-item">
                  <span id="aim-res-reaction" class="stat-value">-- ms</span>
                  <span class="stat-label">${this.isAr ? 'متوسط رد الفعل / الاقتناص' : 'Avg Reaction / TTK'}</span>
                </div>
                <div class="stat-item">
                  <span id="aim-res-false-starts" class="stat-value">0</span>
                  <span class="stat-label">${this.isAr ? 'أخطاء التسرع (False Starts)' : 'False Starts'}</span>
                </div>
              </div>

              <!-- Evidence-Informed Coaching Insights -->
              <div class="aim-coaching-box">
                <h3 class="aim-coaching-heading">${this.isAr ? 'تشخيص المهارات وتوجيهات التطوير' : 'Evidence-Informed Coaching Diagnostics'}</h3>
                
                <div class="aim-coaching-row">
                  <div class="aim-coaching-col">
                    <span class="aim-coaching-tag positive">${this.isAr ? 'أبرز نقاط القوة' : 'Strongest Skill'}</span>
                    <p id="aim-strong-skill" class="aim-coaching-text">--</p>
                  </div>
                  <div class="aim-coaching-col">
                    <span class="aim-coaching-tag critique">${this.isAr ? 'المهارة التي تتطلب التركيز' : 'Weakest Skill to Target'}</span>
                    <p id="aim-weak-skill" class="aim-coaching-text">--</p>
                  </div>
                </div>

                <div class="aim-advice-card">
                  <span class="aim-advice-icon" aria-hidden="true">💡</span>
                  <p id="aim-coaching-advice" class="aim-advice-text">--</p>
                </div>
              </div>
            </div>

            <footer class="aim-dialog-footer">
              <button id="aim-btn-retry-drill" class="btn-primary" type="button">${this.isAr ? 'إعادة التمرين' : 'Retry Drill'}</button>
              <button id="aim-btn-next-drill" class="btn-secondary" type="button">${this.isAr ? 'الانتقال للتمرين المقترح' : 'Next Recommended Drill'}</button>
            </footer>
          </div>
        </dialog>
      </div>
    `;

    this.mount3DView();
    this.bindEvents();
    this.updateCrosshairStyle();
    this.updateSensitivityDisplay();
  }

  mount3DView() {
    const viewport = document.getElementById('aim-viewport');
    if (!SceneManager.isWebGLAvailable()) {
      viewport.innerHTML = `
        <div class="aim-fallback-card">
          <h3>${this.isAr ? 'متصفحك لا يدعم تسريع WebGL 3D' : 'WebGL 3D Acceleration Not Supported'}</h3>
          <p>${this.isAr ? 'يتطلب مختبر تدريب الإيم دعم WebGL في المتصفح. يرجى تفعيل تسريع الرسوميات للبدء.' : 'Aim Assist Training requires WebGL support. Please enable hardware acceleration in your browser.'}</p>
        </div>
      `;
      return;
    }

    this.sceneManager = new SceneManager(viewport, this.settingsStore);
    this.targetManager = new TargetManager(this.sceneManager.targetsGroup, this.settingsStore, this.sceneManager);
    this.weaponController = new WeaponController(
      this.sceneManager.weaponGroup,
      this.sceneManager,
      this.audioManager,
      this.settingsStore
    );
    this.shootingSystem = new ShootingSystem(
      this.sceneManager.camera,
      this.targetManager,
      this.weaponController,
      this.audioManager,
      this.settingsStore,
      this.sceneManager
    );
    this.trainingEngine = new TrainingEngine(
      this.targetManager,
      this.shootingSystem,
      this.weaponController,
      this.audioManager,
      this.settingsStore,
      this.sessionHistory
    );
    this.inputController = new InputController(
      viewport,
      this.sceneManager.camera,
      this.trainingEngine,
      this.weaponController,
      this.settingsStore,
      this.audioManager
    );

    // Setup Engine Callbacks
    this.trainingEngine.onTick = () => this.renderHUD();
    this.trainingEngine.onHit = (hitData) => this.showHitMarker(hitData);
    this.trainingEngine.onStateChange = (state) => this.handleStateChange(state);
    this.trainingEngine.onFinished = (results) => this.showResults(results);
    this.trainingEngine.onAdaptiveChange = (msg) => this.showAdaptiveToast(msg);
    this.weaponController.onAdsChange = (isAds) => this.updateScopeOverlays(isAds);

    // Render initial HUD numbers immediately on mount
    this.renderHUD();
    this.updateScopeOverlays();

    // Start 60fps RequestAnimationFrame Loop
    this.startLoop();

    // Auto-detect mobile devices for control mode
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
      if (this.settingsStore.get('controlMode') === 'desktop' && window.innerWidth <= 768) {
        this.settingsStore.set('controlMode', 'touch');
      }
    }
    this.updateControlsOverlay();
  }

  updateScopeOverlays(isAds = this.weaponController?.isAds) {
    const sniperScope = document.getElementById('aim-sniper-scope');
    const adsVignette = document.getElementById('aim-ads-vignette');
    const crosshairEl = document.getElementById('aim-crosshair');
    const isSniper = this.weaponController?.currentWeaponKey === 'sniper';

    if (sniperScope) sniperScope.style.display = (isSniper && isAds) ? 'flex' : 'none';
    if (adsVignette) adsVignette.style.display = (!isSniper && isAds) ? 'block' : 'none';
    if (crosshairEl) crosshairEl.style.display = (isSniper && isAds) ? 'none' : 'block';
  }

  updateReloadIndicator() {
    const indicator = document.getElementById('aim-reload-indicator');
    if (!indicator || !this.weaponController) return;

    if (this.weaponController.isReloading) {
      indicator.style.display = 'flex';
      const progress = Math.min(1, Math.max(0, this.weaponController.reloadProgress || 0));
      const circumference = 2 * Math.PI * 22; // r=22 => 138.23
      const offset = circumference * (1 - progress);
      const progressBar = document.getElementById('aim-reload-svg-bar');
      if (progressBar) {
        progressBar.style.strokeDasharray = `${circumference.toFixed(2)}px`;
        progressBar.style.strokeDashoffset = `${offset.toFixed(2)}px`;
      }
    } else {
      indicator.style.display = 'none';
    }
  }

  startLoop() {
    const loop = (timestamp) => {
      const delta = Math.min((timestamp - this.lastTime) / 1000, 0.1);
      this.lastTime = timestamp;

      if (this.sceneManager && this.trainingEngine) {
        this.inputController.update(delta, timestamp);
        this.targetManager.update(delta);
        this.weaponController.update(delta, timestamp);
        this.trainingEngine.update(delta, timestamp);
        this.sceneManager.updateParticles(delta);
        this.sceneManager.render();
        this.updateScopeOverlays();
        this.updateReloadIndicator();
      }

      this.animationFrameId = requestAnimationFrame(loop);
    };
    this.animationFrameId = requestAnimationFrame(loop);
  }

  requestFullscreenSimple() {
    const el = document.getElementById('aim-viewport');
    if (!el || document.fullscreenElement) return;
    try {
      const req = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
      if (req) {
        const p = req.call(el);
        if (p && typeof p.catch === 'function') {
          p.catch(() => {});
        }
      }
    } catch (_) {
      // Silent catch - no spam
    }
  }

  toggleFullscreen() {
    const el = document.getElementById('aim-viewport');
    if (!el) return;
    if (!document.fullscreenElement) {
      this.requestFullscreenSimple();
    } else {
      try {
        const exit = document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen;
        if (exit) {
          const p = exit.call(document);
          if (p && typeof p.catch === 'function') p.catch(() => {});
        }
      } catch (_) {}
    }
  }

  updateFullscreenUI(isFs = !!document.fullscreenElement) {
    const mainBtn = document.getElementById('aim-btn-fullscreen');
    const vpBtn = document.getElementById('aim-viewport-fs-btn');
    const label = document.getElementById('aim-btn-fullscreen-label');

    const text = isFs
      ? (this.isAr ? 'خروج من الشاشة' : 'Exit Fullscreen')
      : (this.isAr ? 'ملء الشاشة' : 'Fullscreen');

    if (label) label.textContent = text;

    [mainBtn, vpBtn].forEach((btn) => {
      if (!btn) return;
      btn.classList.toggle('is-fullscreen', isFs);
      const enterIcon = btn.querySelector('.aim-fs-icon-enter');
      const exitIcon = btn.querySelector('.aim-fs-icon-exit');
      const badgeText = btn.querySelector('.aim-fs-badge-text');

      if (enterIcon) enterIcon.style.display = isFs ? 'none' : 'block';
      if (exitIcon) exitIcon.style.display = isFs ? 'block' : 'none';
      if (badgeText) badgeText.textContent = text;
    });
  }

  startDrill() {
    const mode = document.getElementById('aim-mode-select')?.value || 'gridshot';
    const program = document.getElementById('aim-program-select')?.value || 'warmup';
    this.trainingEngine.start(mode, program);
  }

  bindEvents() {
    // Hero play button on game viewport - auto full screen on start without spam
    document.getElementById('aim-btn-hero-play')?.addEventListener('click', () => {
      document.activeElement?.blur();
      this.canvas?.focus();
      this.startDrill();
      this.requestFullscreenSimple();
      if (this.inputController) {
        this.inputController.requestPointerLock();
      }
    });

    // Quick Weapon Selector Pills
    const weaponPills = this.container.querySelectorAll('.aim-weapon-pill');
    weaponPills.forEach((pill) => {
      pill.addEventListener('click', () => {
        const weaponId = pill.getAttribute('data-weapon');
        if (weaponId) {
          this.settingsStore.set('selectedWeapon', weaponId);
          weaponPills.forEach((p) => p.classList.toggle('active', p === pill));
          this.renderHUD();
        }
      });
    });

    document.getElementById('aim-mode-select')?.addEventListener('change', (e) => {
      if (this.trainingEngine.state === 'idle') {
        this.trainingEngine.currentMode = e.target.value;
      }
    });

    document.getElementById('aim-game-select')?.addEventListener('change', (e) => {
      const gameId = e.target.value;
      this.settingsStore.set('selectedGame', gameId);
      const preset = GAME_PRESETS[gameId];
      if (preset) {
        this.settingsStore.set('sens', preset.defaultSens);
        this.settingsStore.set('dpi', preset.defaultDpi);
        this.settingsStore.set('fov', preset.defaultFov);
        this.sceneManager?.updateFov(preset.defaultFov);
        this.updateSensitivityDisplay();
      }
    });

    // Pause menu buttons - Normal resume without auto fullscreen
    document.getElementById('aim-btn-resume')?.addEventListener('click', () => this.trainingEngine.resume());
    document.getElementById('aim-btn-restart')?.addEventListener('click', () => this.trainingEngine.restart());
    document.getElementById('aim-btn-pause-settings')?.addEventListener('click', () => {
      document.getElementById('aim-settings-dialog')?.showModal();
    });

    // Settings dialog buttons
    document.getElementById('aim-btn-settings')?.addEventListener('click', () => {
      this.syncSettingsForm();
      document.getElementById('aim-settings-dialog')?.showModal();
    });

    document.getElementById('aim-btn-history')?.addEventListener('click', () => {
      this.showHistoryModal();
    });

    // Fullscreen Toggle Buttons (Header and In-Viewport)
    document.getElementById('aim-btn-fullscreen')?.addEventListener('click', () => this.toggleFullscreen());
    document.getElementById('aim-viewport-fs-btn')?.addEventListener('click', () => this.toggleFullscreen());

    this._fsChangeHandler = () => this.updateFullscreenUI();
    document.addEventListener('fullscreenchange', this._fsChangeHandler);
    document.addEventListener('webkitfullscreenchange', this._fsChangeHandler);

    // Results modal buttons
    document.getElementById('aim-results-close-btn')?.addEventListener('click', () => {
      document.getElementById('aim-results-dialog')?.close();
    });

    document.getElementById('aim-btn-retry-drill')?.addEventListener('click', () => {
      document.getElementById('aim-results-dialog')?.close();
      this.trainingEngine.restart();
      this.requestFullscreenSimple();
    });

    document.getElementById('aim-btn-next-drill')?.addEventListener('click', (e) => {
      const nextId = e.currentTarget.dataset.nextDrill || 'precision';
      document.getElementById('aim-results-dialog')?.close();
      document.getElementById('aim-mode-select').value = nextId;
      this.trainingEngine.start(nextId);
    });

    // Mobile Virtual Touch Controls Bindings
    this.setupMobileTouchListeners();

    // Settings Form Inputs
    this.setupSettingsInputListeners();
  }

  setupMobileTouchListeners() {
    const touchStick = document.getElementById('aim-touch-stick');
    const touchLook = document.getElementById('aim-touch-look');
    const knob = document.getElementById('aim-touch-knob');

    if (touchStick) {
      touchStick.addEventListener('pointerdown', (e) => {
        touchStick.setPointerCapture(e.pointerId);
        this.inputController.handleTouchMoveStart(e.pointerId, e.clientX, e.clientY);
      });
      touchStick.addEventListener('pointermove', (e) => {
        this.inputController.handleTouchMoveUpdate(e.pointerId, e.clientX, e.clientY);
        if (knob) {
          const vec = this.inputController.touchMoveVector;
          knob.style.transform = `translate(${vec.x * 32}px, ${vec.y * 32}px)`;
        }
      });
      const endStick = (e) => {
        this.inputController.handleTouchMoveEnd(e.pointerId);
        if (knob) knob.style.transform = 'translate(0, 0)';
      };
      touchStick.addEventListener('pointerup', endStick);
      touchStick.addEventListener('pointercancel', endStick);
    }

    if (touchLook) {
      touchLook.addEventListener('pointerdown', (e) => {
        touchLook.setPointerCapture(e.pointerId);
        this.inputController.handleTouchLookStart(e.pointerId, e.clientX, e.clientY);
      });
      touchLook.addEventListener('pointermove', (e) => {
        this.inputController.handleTouchLookUpdate(e.pointerId, e.clientX, e.clientY);
      });
      const endLook = (e) => this.inputController.handleTouchLookEnd(e.pointerId);
      touchLook.addEventListener('pointerup', endLook);
      touchLook.addEventListener('pointercancel', endLook);
    }

    document.getElementById('aim-btn-touch-fire')?.addEventListener('pointerdown', () => {
      if (this.trainingEngine.state === 'active') {
        this.trainingEngine.handlePlayerFire(performance.now(), this.inputController.velocity.length(), true);
      }
    });

    document.getElementById('aim-btn-touch-ads')?.addEventListener('click', () => {
      this.weaponController.setAds(!this.weaponController.isAds);
    });

    document.getElementById('aim-btn-touch-reload')?.addEventListener('click', () => {
      this.weaponController.reload();
    });
  }

  setupSettingsInputListeners() {
    const sensInput = document.getElementById('aim-sens-input');
    const dpiInput = document.getElementById('aim-dpi-input');
    const fovInput = document.getElementById('aim-fov-input');
    const adsInput = document.getElementById('aim-ads-input');

    sensInput?.addEventListener('input', (e) => {
      this.settingsStore.set('sens', Number(e.target.value) || 1.0);
      this.updateSensitivityDisplay();
    });

    dpiInput?.addEventListener('input', (e) => {
      this.settingsStore.set('dpi', Number(e.target.value) || 800);
      this.updateSensitivityDisplay();
    });

    fovInput?.addEventListener('input', (e) => {
      const fov = Number(e.target.value) || 103;
      this.settingsStore.set('fov', fov);
      document.getElementById('aim-fov-val').textContent = fov + '°';
      this.sceneManager?.updateFov(fov);
    });

    adsInput?.addEventListener('input', (e) => {
      const ads = Number(e.target.value) || 0.85;
      this.settingsStore.set('adsMultiplier', ads);
      document.getElementById('aim-ads-val').textContent = ads.toFixed(2) + 'x';
    });

    document.getElementById('aim-invert-y')?.addEventListener('change', (e) => {
      this.settingsStore.set('invertY', e.target.checked);
    });

    document.getElementById('aim-adaptive-check')?.addEventListener('change', (e) => {
      this.settingsStore.set('adaptiveDifficulty', e.target.checked);
    });

    document.getElementById('aim-follow-recoil')?.addEventListener('change', (e) => {
      this.settingsStore.set('followRecoil', e.target.checked);
      if (!e.target.checked && this.inputController) {
        this.inputController.updateCrosshairRecoilOffset(0, 0);
      }
    });

    document.getElementById('aim-cross-style')?.addEventListener('change', (e) => {
      this.settingsStore.set('crosshairStyle', e.target.value);
      this.updateCrosshairStyle();
    });

    document.getElementById('aim-cross-color')?.addEventListener('change', (e) => {
      this.settingsStore.set('crosshairColor', e.target.value);
      this.updateCrosshairStyle();
    });

    document.getElementById('aim-target-color-select')?.addEventListener('change', (e) => {
      this.settingsStore.set('targetColor', e.target.value);
    });

    document.getElementById('aim-control-mode-select')?.addEventListener('change', (e) => {
      this.settingsStore.set('controlMode', e.target.value);
      this.updateControlsOverlay();
    });

    document.getElementById('aim-touch-sens-y')?.addEventListener('input', (e) => {
      const val = Number(e.target.value) || 1.0;
      this.settingsStore.set('touchSensY', val);
      const valLbl = document.getElementById('aim-touch-sens-y-val');
      if (valLbl) valLbl.textContent = val.toFixed(1) + 'x';
    });

    document.getElementById('aim-gyro-check')?.addEventListener('change', async (e) => {
      this.settingsStore.set('gyroEnabled', e.target.checked);
      if (e.target.checked && this.inputController?.enableGyro) {
        await this.inputController.enableGyro();
      }
    });

    document.getElementById('aim-sound-vol')?.addEventListener('input', (e) => {
      this.settingsStore.set('soundVolume', Number(e.target.value));
    });

    // Quick Telemetry Toolbar Listeners
    const quickMoving = document.getElementById('aim-quick-moving-check');
    const quickShape = document.getElementById('aim-quick-shape-select');
    const quickMotion = document.getElementById('aim-quick-motion-select');
    const quickSpeed = document.getElementById('aim-quick-speed-select');
    const quickRange = document.getElementById('aim-quick-range-select');

    quickMoving?.addEventListener('change', (e) => {
      this.settingsStore.set('movingTargetsEnabled', e.target.checked);
      const dlgCheck = document.getElementById('aim-moving-check');
      if (dlgCheck) dlgCheck.checked = e.target.checked;
    });

    quickShape?.addEventListener('change', (e) => {
      this.settingsStore.set('targetShape', e.target.value);
      const dlgShape = document.getElementById('aim-shape-select');
      if (dlgShape) dlgShape.value = e.target.value;
    });

    quickMotion?.addEventListener('change', (e) => {
      this.settingsStore.set('motionPattern', e.target.value);
      const dlgMotion = document.getElementById('aim-motion-select');
      if (dlgMotion) dlgMotion.value = e.target.value;
    });

    quickSpeed?.addEventListener('change', (e) => {
      const spd = Number(e.target.value) || 1.0;
      this.settingsStore.set('targetSpeed', spd);
      const dlgSpeed = document.getElementById('aim-speed-input');
      if (dlgSpeed) dlgSpeed.value = spd;
      const dlgSpeedVal = document.getElementById('aim-speed-val');
      if (dlgSpeedVal) dlgSpeedVal.textContent = spd.toFixed(1) + 'x';
    });

    quickRange?.addEventListener('change', (e) => {
      this.settingsStore.set('targetRangeZone', e.target.value);
      const dlgRange = document.getElementById('aim-range-zone-select');
      if (dlgRange) dlgRange.value = e.target.value;
    });

    // Settings Dialog Target Mechanics Listeners
    const shapeSelect = document.getElementById('aim-shape-select');
    const motionSelect = document.getElementById('aim-motion-select');
    const speedInput = document.getElementById('aim-speed-input');
    const sizeInput = document.getElementById('aim-size-input');
    const movingCheck = document.getElementById('aim-moving-check');
    const rangeSelect = document.getElementById('aim-range-zone-select');

    shapeSelect?.addEventListener('change', (e) => {
      this.settingsStore.set('targetShape', e.target.value);
      if (quickShape) quickShape.value = e.target.value;
    });

    motionSelect?.addEventListener('change', (e) => {
      this.settingsStore.set('motionPattern', e.target.value);
      if (quickMotion) quickMotion.value = e.target.value;
    });

    rangeSelect?.addEventListener('change', (e) => {
      this.settingsStore.set('targetRangeZone', e.target.value);
      if (quickRange) quickRange.value = e.target.value;
    });

    speedInput?.addEventListener('input', (e) => {
      const spd = Number(e.target.value) || 1.0;
      this.settingsStore.set('targetSpeed', spd);
      const speedVal = document.getElementById('aim-speed-val');
      if (speedVal) speedVal.textContent = spd.toFixed(1) + 'x';
      if (quickSpeed) quickSpeed.value = spd.toString();
    });

    sizeInput?.addEventListener('input', (e) => {
      const sz = Number(e.target.value) || 1.0;
      this.settingsStore.set('targetSize', sz);
      const sizeVal = document.getElementById('aim-size-val');
      if (sizeVal) sizeVal.textContent = sz.toFixed(1) + 'x';
    });

    movingCheck?.addEventListener('change', (e) => {
      this.settingsStore.set('movingTargetsEnabled', e.target.checked);
      if (quickMoving) quickMoving.checked = e.target.checked;
    });

    document.getElementById('aim-btn-reset-settings')?.addEventListener('click', () => {
      this.settingsStore.reset();
      this.syncSettingsForm();
      this.updateCrosshairStyle();
      this.updateSensitivityDisplay();
      this.updateControlsOverlay();
    });
  }

  syncSettingsForm() {
    const s = this.settingsStore.state;
    const sensInput = document.getElementById('aim-sens-input');
    const dpiInput = document.getElementById('aim-dpi-input');
    const fovInput = document.getElementById('aim-fov-input');
    const adsInput = document.getElementById('aim-ads-input');
    const invertY = document.getElementById('aim-invert-y');
    const adaptiveCheck = document.getElementById('aim-adaptive-check');
    const followRecoil = document.getElementById('aim-follow-recoil');
    const crossStyle = document.getElementById('aim-cross-style');
    const crossColor = document.getElementById('aim-cross-color');
    const targetColor = document.getElementById('aim-target-color-select');
    const controlMode = document.getElementById('aim-control-mode-select');
    const touchSensY = document.getElementById('aim-touch-sens-y');
    const touchSensYVal = document.getElementById('aim-touch-sens-y-val');
    const gyroCheck = document.getElementById('aim-gyro-check');
    const soundVol = document.getElementById('aim-sound-vol');

    const shapeSelect = document.getElementById('aim-shape-select');
    const rangeSelect = document.getElementById('aim-range-zone-select');
    const motionSelect = document.getElementById('aim-motion-select');
    const speedInput = document.getElementById('aim-speed-input');
    const sizeInput = document.getElementById('aim-size-input');
    const movingCheck = document.getElementById('aim-moving-check');
    const speedVal = document.getElementById('aim-speed-val');
    const sizeVal = document.getElementById('aim-size-val');

    const quickMoving = document.getElementById('aim-quick-moving-check');
    const quickShape = document.getElementById('aim-quick-shape-select');
    const quickMotion = document.getElementById('aim-quick-motion-select');
    const quickSpeed = document.getElementById('aim-quick-speed-select');
    const quickRange = document.getElementById('aim-quick-range-select');

    if (sensInput) sensInput.value = s.sens;
    if (dpiInput) dpiInput.value = s.dpi;
    if (fovInput) fovInput.value = s.fov;
    if (adsInput) adsInput.value = s.adsMultiplier;
    if (invertY) invertY.checked = s.invertY;
    if (adaptiveCheck) adaptiveCheck.checked = s.adaptiveDifficulty;
    if (followRecoil) followRecoil.checked = !!s.followRecoil;
    if (crossStyle) crossStyle.value = s.crosshairStyle;
    if (crossColor) crossColor.value = s.crosshairColor;
    if (targetColor) targetColor.value = s.targetColor;
    if (controlMode) controlMode.value = s.controlMode;
    if (touchSensY) touchSensY.value = s.touchSensY || 1.0;
    if (touchSensYVal) touchSensYVal.textContent = (s.touchSensY || 1.0).toFixed(1) + 'x';
    if (gyroCheck) gyroCheck.checked = !!s.gyroEnabled;
    if (soundVol) soundVol.value = s.soundVolume;

    if (shapeSelect) shapeSelect.value = s.targetShape || 'mixed';
    if (rangeSelect) rangeSelect.value = s.targetRangeZone || 'all';
    if (motionSelect) motionSelect.value = s.motionPattern || 'auto';
    if (speedInput) speedInput.value = s.targetSpeed || 1.0;
    if (sizeInput) sizeInput.value = s.targetSize || 1.0;
    if (movingCheck) movingCheck.checked = s.movingTargetsEnabled !== false;
    if (speedVal) speedVal.textContent = (s.targetSpeed || 1.0).toFixed(1) + 'x';
    if (sizeVal) sizeVal.textContent = (s.targetSize || 1.0).toFixed(1) + 'x';

    if (quickMoving) quickMoving.checked = s.movingTargetsEnabled !== false;
    if (quickShape) quickShape.value = s.targetShape || 'mixed';
    if (quickRange) quickRange.value = s.targetRangeZone || 'all';
    if (quickMotion) quickMotion.value = s.motionPattern || 'auto';
    if (quickSpeed) quickSpeed.value = (s.targetSpeed || 1.0).toString();

    document.getElementById('aim-fov-val').textContent = s.fov + '°';
    document.getElementById('aim-ads-val').textContent = s.adsMultiplier.toFixed(2) + 'x';
    this.updateSensitivityDisplay();
  }

  updateSensitivityDisplay() {
    const cm360 = this.settingsStore.calculateCm360();
    const badge = document.getElementById('aim-cm360-val');
    if (badge) {
      badge.textContent = cm360 > 0 ? `${cm360} cm/360` : '--';
    }
  }

  updateCrosshairStyle() {
    const crosshairEl = document.getElementById('aim-crosshair');
    if (!crosshairEl) return;

    const style = this.settingsStore.get('crosshairStyle') || 'cross';
    const color = this.settingsStore.get('crosshairColor') || '#38bdf8';

    crosshairEl.className = `aim-crosshair crosshair-${style}`;
    crosshairEl.style.color = color;
  }

  updateControlsOverlay() {
    const isTouch = this.settingsStore.get('controlMode') === 'touch';
    const overlay = document.getElementById('aim-mobile-controls');
    if (overlay) {
      overlay.style.display = isTouch ? 'flex' : 'none';
    }
  }

  showHitMarker(hitData) {
    const marker = document.getElementById('aim-hit-marker');
    if (!marker) return;
    marker.className = 'aim-hit-marker';
    if (hitData && (hitData.isBullseye || hitData.isHeadshot)) {
      marker.classList.add('bullseye');
    }
    marker.classList.add('active');
    clearTimeout(this._hitMarkerTimeout);
    this._hitMarkerTimeout = setTimeout(() => {
      marker.classList.remove('active', 'bullseye');
    }, 120);
  }

  handleStateChange(state) {
    const heroOverlay = document.getElementById('aim-viewport-hero');
    const pauseMenu = document.getElementById('aim-pause-menu');
    const countdownOverlay = document.getElementById('aim-countdown-overlay');

    if (heroOverlay) {
      heroOverlay.style.display = (state === 'idle' || state === 'finished') ? 'flex' : 'none';
    }
    if (pauseMenu) pauseMenu.style.display = state === 'paused' ? 'flex' : 'none';
    if (countdownOverlay) countdownOverlay.style.display = state === 'countdown' ? 'flex' : 'none';
  }

  renderHUD() {
    const scoreEl = document.getElementById('aim-hud-score');
    const timerEl = document.getElementById('aim-hud-timer');
    const accEl = document.getElementById('aim-hud-acc');
    const ammoEl = document.getElementById('aim-hud-ammo');
    const ammoPips = document.getElementById('aim-hud-ammo-pips');
    const countdownNum = document.getElementById('aim-countdown-number');
    const reactionPrompt = document.getElementById('aim-reaction-prompt');
    const reactionText = document.getElementById('aim-reaction-text');
    const sniperScope = document.getElementById('aim-sniper-scope');
    const crosshairEl = document.getElementById('aim-crosshair');

    if (scoreEl) scoreEl.textContent = this.trainingEngine.metrics.score;
    if (timerEl) timerEl.textContent = Math.ceil(this.trainingEngine.timeRemaining) + 's';
    if (accEl) accEl.textContent = this.trainingEngine.metrics.accuracy + '%';

    if (ammoEl && this.weaponController) {
      if (this.weaponController.isReloading) {
        ammoEl.textContent = this.isAr ? 'تلقيم...' : 'RELOAD...';
        ammoEl.className = 'aim-hud-val reloading';
      } else {
        ammoEl.textContent = `${this.weaponController.ammo}/${this.weaponController.maxAmmo}`;
        const isLow = this.weaponController.ammo <= Math.ceil(this.weaponController.maxAmmo * 0.25);
        ammoEl.className = `aim-hud-val ${isLow ? 'low-ammo' : ''}`;
      }
    }

    if (ammoPips && this.weaponController) {
      const total = Math.min(this.weaponController.maxAmmo, 30);
      const current = Math.min(this.weaponController.ammo, total);
      let pipsHtml = '';
      for (let i = 0; i < total; i++) {
        pipsHtml += `<span class="aim-pip ${i < current ? 'active' : 'spent'}"></span>`;
      }
      ammoPips.innerHTML = pipsHtml;
    }

    const adsVignette = document.getElementById('aim-ads-vignette');
    if (this.weaponController) {
      const isAds = this.weaponController.isAds;
      const isSniper = this.weaponController.currentWeaponKey === 'sniper';
      if (sniperScope) sniperScope.style.display = (isSniper && isAds) ? 'flex' : 'none';
      if (adsVignette) adsVignette.style.display = (!isSniper && isAds) ? 'block' : 'none';
      if (crosshairEl) crosshairEl.style.display = (isSniper && isAds) ? 'none' : 'block';
    }

    if (countdownNum) countdownNum.textContent = this.trainingEngine.countdownSeconds;

    // Live Telemetry Dashboard updates
    const liveHits = document.getElementById('aim-live-hits');
    const liveMisses = document.getElementById('aim-live-misses');
    const liveAcc = document.getElementById('aim-live-acc');
    const liveAccBar = document.getElementById('aim-live-acc-bar');
    const liveSpeed = document.getElementById('aim-live-speed');
    const liveStreak = document.getElementById('aim-live-streak');
    const liveBullseyes = document.getElementById('aim-live-bullseyes');
    const shotPills = document.getElementById('aim-shot-pills');

    if (liveHits) liveHits.textContent = this.trainingEngine.metrics.hits;
    if (liveMisses) liveMisses.textContent = this.trainingEngine.metrics.misses;
    if (liveAcc) liveAcc.textContent = this.trainingEngine.metrics.accuracy + '%';
    if (liveAccBar) {
      const acc = Math.min(100, Math.max(0, this.trainingEngine.metrics.accuracy));
      liveAccBar.style.width = acc + '%';
      liveAccBar.style.backgroundColor = acc >= 80 ? '#10b981' : (acc >= 55 ? '#38bdf8' : (acc >= 35 ? '#f59e0b' : '#ef4444'));
    }
    if (liveSpeed) {
      const avg = this.trainingEngine.getAverageReactionTime();
      liveSpeed.textContent = avg > 0 ? avg + ' ms' : '-- ms';
    }
    if (liveStreak) liveStreak.textContent = this.trainingEngine.streak + 'x';
    if (liveBullseyes) liveBullseyes.textContent = this.trainingEngine.metrics.bullseyes;

    if (shotPills && this.trainingEngine.metrics.recentShots) {
      const recent = this.trainingEngine.metrics.recentShots;
      if (recent.length === 0) {
        shotPills.innerHTML = `<span class="aim-shot-feed-empty">${this.isAr ? 'ابدأ التمرين لإظهار تسلسل الطلقات وسرعتها' : 'Start drill to log real-time shot tempo'}</span>`;
      } else {
        shotPills.innerHTML = recent.map((s) => {
          if (s.type === 'bullseye') {
            return `<span class="aim-shot-pill bullseye">🎯 ${s.latency}ms ${this.isAr ? 'مركز' : 'Bullseye'}</span>`;
          } else if (s.type === 'hit') {
            return `<span class="aim-shot-pill hit">✔ ${s.latency}ms ${this.isAr ? 'إصابة' : 'Hit'}</span>`;
          } else {
            return `<span class="aim-shot-pill miss">✖ ${this.isAr ? 'خطأ' : 'Miss'}</span>`;
          }
        }).join('');
      }
    }

    // Reaction prompt handling
    if (reactionPrompt && reactionText) {
      if (this.trainingEngine.currentMode === 'reaction' && this.trainingEngine.state === 'active') {
        reactionPrompt.style.display = 'block';
        if (this.trainingEngine.reactionState === 'waiting_cue') {
          reactionText.textContent = this.isAr ? 'انتظر الإشارة...' : 'WAIT...';
          reactionText.className = 'aim-reaction-text waiting';
        } else {
          reactionText.textContent = this.isAr ? 'أطلق الآن!' : 'FIRE NOW!';
          reactionText.className = 'aim-reaction-text active';
        }
      } else {
        reactionPrompt.style.display = 'none';
      }
    }
  }

  showAdaptiveToast(message) {
    const toast = document.getElementById('aim-adaptive-toast');
    if (toast) {
      toast.textContent = message;
      toast.classList.add('visible');
      setTimeout(() => toast.classList.remove('visible'), 2400);
    }
  }

  showResults(results) {
    const { session, coaching, isNewBest } = results;
    const dialog = document.getElementById('aim-results-dialog');
    if (!dialog) return;

    const tier = getReflexTier(session.score, session.accuracy);
    const tierPill = document.getElementById('aim-tier-pill');
    const finalScore = document.getElementById('aim-final-score');
    const bestBadge = document.getElementById('aim-best-badge');

    if (tierPill) {
      tierPill.textContent = this.isAr ? tier.title_ar : tier.title_en;
      tierPill.style.color = tier.color;
    }
    if (finalScore) finalScore.textContent = session.score;
    if (bestBadge) bestBadge.style.display = isNewBest ? 'inline-block' : 'none';

    document.getElementById('aim-res-acc').textContent = session.accuracy + '%';
    document.getElementById('aim-res-hits').textContent = `${session.hits} / ${session.shots}`;

    const reactionVal = session.reactionMs
      ? `${session.reactionMs} ms`
      : (session.trackingPct ? `${session.trackingPct}% Track` : '--');
    document.getElementById('aim-res-reaction').textContent = reactionVal;
    document.getElementById('aim-res-false-starts').textContent = session.falseStarts || 0;

    // Coaching
    document.getElementById('aim-strong-skill').textContent = coaching.strongestSkill;
    document.getElementById('aim-weak-skill').textContent = coaching.weakestSkill;
    document.getElementById('aim-coaching-advice').textContent = coaching.diagnosticTip;

    const nextDrillBtn = document.getElementById('aim-btn-next-drill');
    if (nextDrillBtn) {
      nextDrillBtn.dataset.nextDrill = coaching.nextDrillId;
    }

    dialog.showModal();

    // Check qualification for Global Top 100 Leaderboard
    if (session && session.score > 0) {
      checkAndPromptScore({
        score: session.score,
        secondaryMetric: session.accuracy || 0,
        telemetry: {
          hits: session.hits || 0,
          shots: session.shots || 0,
          durationSec: session.duration || 60,
          reactionMs: session.reactionMs || 0
        }
      });
    }
  }

  showHistoryModal() {
    const recent = this.sessionHistory.getRecentSessions(10);
    const dialog = document.getElementById('aim-results-dialog');
    if (!dialog) return;

    if (recent.length > 0) {
      const latest = recent[0];
      const tier = getReflexTier(latest.score, latest.accuracy);
      document.getElementById('aim-final-score').textContent = latest.score;
      document.getElementById('aim-tier-pill').textContent = this.isAr ? tier.title_ar : tier.title_en;
      document.getElementById('aim-res-acc').textContent = latest.accuracy + '%';
      document.getElementById('aim-res-hits').textContent = `${latest.hits} / ${latest.shots}`;
      document.getElementById('aim-res-reaction').textContent = latest.reactionMs ? `${latest.reactionMs} ms` : '--';
      dialog.showModal();
    } else {
      alert(this.isAr ? 'لا توجد جلسات تدريبية مسجلة بعد. أكمل تمريناً لحفظ إحصائياتك!' : 'No session records found yet. Complete a drill to view stats!');
    }
  }

  dispose() {
    if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
    if (this._fsChangeHandler) {
      document.removeEventListener('fullscreenchange', this._fsChangeHandler);
      document.removeEventListener('webkitfullscreenchange', this._fsChangeHandler);
    }
    this.sceneManager?.dispose();
    this.inputController?.dispose();
  }
}

export function initAimAssistApp(containerId = 'aim-training-app') {
  mountLeaderboard({
    toolId: 'aim-assist-training',
    metricLabel: 'PTS',
    secondaryLabel: document.documentElement.lang !== 'en' ? 'الدقة' : 'Accuracy',
    isHigherBetter: true
  });
  return new AimAssistApp(containerId);
}
